import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { useTRPCAPI, useTRPCQuery } from '../../../../trpc/trpc';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { smlRoutePath } from '../../../../routing/sml/smlRoutePath';
import { linkedFileUuid } from '../filesRedirectTarget';
import { useWorkLists } from './useWorkLists';
import { workLinkDecision, workLinkNeedsFile } from './workLinkTarget';
import { hasFile } from './workListModel';
import { WORK_PANEL_PARAM } from './useStudioWorkPanel';

/** Studio with the Work panel open, with every value of the old link (`state=`, `result=`, …). */
export function studioWorkPanelPath(search: string): string {
  const params = new URLSearchParams(search);
  params.set(WORK_PANEL_PARAM, '1');
  return `/${smlRoutePath(SMLRoutes.promoteStudio)}?${params.toString()}`;
}

/**
 * The old Work route (`/app/promote/promoteWork`). Navigation v2 (design c 3e): Work is a panel
 * on Studio, so the route opens Studio with the panel open. The emails and pushes keep this path.
 * A link of a finished file (`result=`, `reel=`, or a song with `kind=`) still opens the file in
 * its home first; `workLinkTarget.ts` holds the rules. A uuid in the link is looked up with
 * `assets.locate`, and the lists of the work say if that file is still made or failed.
 */
const WorkLinkRedirectComponent: React.FC = () => {
  const { search } = useLocation();
  const accountId = useAccountId();
  const trpcAPI = useTRPCAPI();
  const uuid = linkedFileUuid(search);
  const song = useMemo(() => new URLSearchParams(search).get('catalogItemUuid') ?? '', [search]);
  const needsFile = workLinkNeedsFile(search);

  const locate = useTRPCQuery(
    trpcAPI.assets.locate.queryOptions(
      { accountId: accountId ?? 0, uuid },
      { enabled: accountId !== null && needsFile, retry: false, staleTime: Infinity }
    )
  );
  const lists = useWorkLists({
    accountId,
    catalogItemUuid: song === '' ? undefined : song,
    withDone: false,
    enabled: needsFile,
  });

  if (accountId === null || (needsFile && (locate.isLoading || lists.isLoading))) {
    return <LoadingView />;
  }
  const decision = workLinkDecision({
    search,
    located: uuid === '' ? undefined : (locate.data ?? null),
    isBusy: hasFile(uuid, lists.busy, lists.failed),
  });
  return <Navigate to={decision.kind === 'page' ? studioWorkPanelPath(search) : decision.to} replace />;
};

export const WorkLinkRedirect = TypedMemo(WorkLinkRedirectComponent);
